import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type {
  BienCreateInput,
  BienElementCreateInput,
  BienListParams,
  EspaceElementCreateInput,
  NiveauCreateInput,
  QuantityCreateInput,
  QuantityPreviewInput,
  StructureCreateInput,
} from '../../lib/api';

/**
 * Query keys for the LOT 4 property-structure hierarchy. Everything lives under
 * the `['structure']` root so hierarchy mutations can invalidate in one shot.
 */
export const structureKeys = {
  all: ['structure'] as const,
  biens: (dossierId: string, params: BienListParams) =>
    ['structure', 'biens', dossierId, params] as const,
  structures: (bienId: string) => ['structure', 'structures', bienId] as const,
  niveaux: (structureId: string) => ['structure', 'niveaux', structureId] as const,
  elements: (bienId: string) => ['structure', 'elements', bienId] as const,
  quantities: (elementId: string) => ['structure', 'quantities', elementId] as const,
  formulas: ['quantity-formulas'] as const,
};

export function useBiens(dossierId: string, params: BienListParams) {
  const api = useApi();
  return useQuery({
    queryKey: structureKeys.biens(dossierId, params),
    queryFn: () => api.listBiens(dossierId, params),
  });
}

export function useStructures(bienId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: structureKeys.structures(bienId),
    queryFn: () => api.listStructures(bienId),
    enabled,
  });
}

export function useNiveaux(structureId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: structureKeys.niveaux(structureId),
    queryFn: () => api.listNiveaux(structureId),
    enabled,
  });
}

export function useElements(bienId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: structureKeys.elements(bienId),
    queryFn: () => api.listElements(bienId),
    enabled,
  });
}

export function useQuantities(elementId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: structureKeys.quantities(elementId),
    queryFn: () => api.listQuantities(elementId),
    enabled,
  });
}

export function useQuantityFormulas(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: structureKeys.formulas,
    queryFn: () => api.listQuantityFormulas(true),
    enabled,
  });
}

export function useCreateBien(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BienCreateInput) => api.createBien(dossierId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.all }),
  });
}

export function useCreateStructure(bienId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StructureCreateInput) => api.createStructure(bienId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.all }),
  });
}

export function useCreateNiveau(structureId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NiveauCreateInput) => api.createNiveau(structureId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.all }),
  });
}

export function useCreateEspaceElement(niveauId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EspaceElementCreateInput) => api.createEspaceElement(niveauId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.all }),
  });
}

export function useCreateBienElement(bienId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BienElementCreateInput) => api.createBienElement(bienId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.all }),
  });
}

export function useCreateQuantity(elementId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuantityCreateInput) => api.createQuantity(elementId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.quantities(elementId) }),
  });
}

/** Stateless server-side preview — never computed locally. */
export function usePreviewQuantity() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: QuantityPreviewInput) => api.previewQuantity(input),
  });
}

export function useValidateQuantity(elementId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quantityId: string) => api.validateQuantity(quantityId),
    onSuccess: () => qc.invalidateQueries({ queryKey: structureKeys.quantities(elementId) }),
  });
}
